import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';

interface UserData {
  id: number;
  clerkId: string;
  email: string;
  name: string;
  subscriptionTier: 'free' | 'trial' | 'basic' | 'elite' | 'admin';
  subscriptionStatus: string;
  effectiveTier: string;
  scansToday: number;
  maxScans: number;
  remainingScans: number;
  canScan: boolean;
  promoCodeUsed: string | null;
  promoExpiresAt: string | null;
  hasValidPromo: boolean;
  // Trial fields
  hasValidTrial: boolean;
  trialDaysRemaining: number;
  canStartTrial: boolean;
  trialExpiresAt: string | null;
  hasUsedTrial: boolean;
}

interface UserContextType {
  userData: UserData | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  incrementScan: (ticker: string, score: number, status: string) => Promise<boolean>;
  applyPromoCode: (code: string) => Promise<{ success: boolean; message: string }>;
  startFreeTrial: () => Promise<{ success: boolean; message: string }>;
}

const defaultUserData: UserData = {
  id: 0,
  clerkId: '',
  email: '',
  name: '',
  subscriptionTier: 'free',
  subscriptionStatus: 'inactive',
  effectiveTier: 'free',
  scansToday: 0,
  maxScans: 0,
  remainingScans: 0,
  canScan: false,
  promoCodeUsed: null,
  promoExpiresAt: null,
  hasValidPromo: false,
  hasValidTrial: false,
  trialDaysRemaining: 0,
  canStartTrial: true,
  trialExpiresAt: null,
  hasUsedTrial: false,
};

const UserContext = createContext<UserContextType>({
  userData: null,
  loading: true,
  error: null,
  refreshUser: async () => {},
  incrementScan: async () => false,
  applyPromoCode: async () => ({ success: false, message: '' }),
  startFreeTrial: async () => ({ success: false, message: '' }),
});

export const useUserData = () => useContext(UserContext);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const { user, isLoaded: clerkLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserData = useCallback(async () => {
    if (!clerkLoaded || !isSignedIn || !user) {
      setUserData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, ensure user exists in our database
      const createRes = await fetch('/api/user?action=create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clerkId: user.id,
          email: user.primaryEmailAddress?.emailAddress || '',
          name: user.fullName || user.firstName || '',
        }),
      });

      if (!createRes.ok) {
        throw new Error('Failed to create/update user');
      }

      // Then fetch full user data
      const res = await fetch(`/api/user?action=get&clerkId=${user.id}`);
      const data = await res.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const hasValidPromo = data.promo_code_used &&
        data.promo_expires_at &&
        new Date(data.promo_expires_at) > new Date();

      setUserData({
        id: data.id,
        clerkId: data.clerk_id,
        email: data.email,
        name: data.name,
        subscriptionTier: data.subscription_tier,
        subscriptionStatus: data.subscription_status,
        effectiveTier: data.effectiveTier,
        scansToday: data.scans_today,
        maxScans: data.maxScans,
        remainingScans: data.remainingScans,
        canScan: data.canScan,
        promoCodeUsed: data.promo_code_used,
        promoExpiresAt: data.promo_expires_at,
        hasValidPromo,
        // Trial fields
        hasValidTrial: data.hasValidTrial || false,
        trialDaysRemaining: data.trialDaysRemaining || 0,
        canStartTrial: data.canStartTrial || false,
        trialExpiresAt: data.trial_expires_at || null,
        hasUsedTrial: data.has_used_trial || false,
      });
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  }, [clerkLoaded, isSignedIn, user]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const incrementScan = useCallback(async (ticker: string, score: number, status: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const res = await fetch('/api/user?action=increment-scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clerkId: user.id,
          ticker,
          score,
          status,
        }),
      });

      const data = await res.json();

      if (data.error) {
        if (data.remainingScans === 0) {
          setError('Daily scan limit reached. Upgrade your plan for more scans!');
        }
        return false;
      }

      // Update local state
      setUserData(prev => prev ? {
        ...prev,
        scansToday: data.scansToday,
        remainingScans: data.remainingScans,
        canScan: data.remainingScans > 0,
      } : null);

      return true;
    } catch (err) {
      console.error('Error incrementing scan:', err);
      return false;
    }
  }, [user]);

  const applyPromoCode = useCallback(async (code: string): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Not signed in' };

    try {
      const res = await fetch('/api/user?action=apply-promo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clerkId: user.id,
          code,
        }),
      });

      const data = await res.json();

      if (data.error) {
        return { success: false, message: data.error };
      }

      // Refresh user data to get updated promo status
      await fetchUserData();

      return { success: true, message: `Promo code applied! You now have ${data.tier} access until ${new Date(data.expiresAt).toLocaleDateString()}.` };
    } catch (err) {
      return { success: false, message: 'Failed to apply promo code' };
    }
  }, [user, fetchUserData]);

  const startFreeTrial = useCallback(async (): Promise<{ success: boolean; message: string }> => {
    if (!user) return { success: false, message: 'Not signed in' };

    try {
      const res = await fetch('/api/user?action=start-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clerkId: user.id,
        }),
      });

      const data = await res.json();

      if (data.error) {
        return { success: false, message: data.error };
      }

      // Refresh user data to get updated trial status
      await fetchUserData();

      return { success: true, message: data.message };
    } catch (err) {
      return { success: false, message: 'Failed to start free trial' };
    }
  }, [user, fetchUserData]);

  return (
    <UserContext.Provider value={{
      userData,
      loading,
      error,
      refreshUser: fetchUserData,
      incrementScan,
      applyPromoCode,
      startFreeTrial,
    }}>
      {children}
    </UserContext.Provider>
  );
};
